'use client';

import { c as _c } from "react/compiler-runtime";
import { useActiveWallet } from '@morfi/backend-sdk-v2/client';
import Image from 'next/image'; // ALLOW-RAW-IMG: static USDC watermark in devnet faucet banner, not user content
import { useTranslations } from 'next-intl';
import { type JSX, useEffect, useState } from 'react';
import { usePortfolioContext } from '@/components/portfolio/portfolio-context';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Panel, PanelContent } from '@/components/ui/panel';
import { type FaucetClaimFailure, type FaucetClaimResult, useFaucetClaim } from '@/hooks/faucet/use-faucet-claim';
import { cooldownDisplay } from '@/lib/faucet/cooldown';
import { useNetworkMode } from '@/lib/network/use-network-mode';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';
import { useFaucetCooldownStore } from '@/stores/faucet-cooldown';
import type { Wallet } from '@/types/auth';
export interface FaucetBannerProps {
  /** All wallets known to the portfolio (archived + active). */
  wallets: Wallet[];
  /** Retained for the portfolio-body contract; faucet routing never falls back to it. */
  primaryWallet: Wallet | null;
}
export function FaucetBanner(t0) {
  const $ = _c(44);
  const {
    wallets
  } = t0;
  const t = useTranslations("portfolio.faucet_banner");
  const tFaucet = useTranslations("faucet");
  const network = useNetworkMode();
  const {
    viewerMode
  } = usePortfolioContext();
  const {
    activeWalletId
  } = useActiveWallet();
  const {
    enabled: multiWalletEnabled,
    primaryWallet
  } = useMultiWalletFeature();
  const {
    claim,
    isPending
  } = useFaucetClaim("portfolio_banner");
  let t1;
  if ($[0] !== activeWalletId || $[1] !== multiWalletEnabled || $[2] !== primaryWallet || $[3] !== wallets) {
    t1 = multiWalletEnabled ? wallets.find(wallet => wallet.id === activeWalletId && wallet.archivedAt === null && wallet.isAvailable !== false) ?? null : primaryWallet !== null && primaryWallet.archivedAt === null && primaryWallet.isAvailable !== false ? primaryWallet : null;
    $[0] = activeWalletId;
    $[1] = multiWalletEnabled;
    $[2] = primaryWallet;
    $[3] = wallets;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const destination = t1;
  const destinationAddress = destination?.address ?? null;
  let t2;
  if ($[5] !== destinationAddress) {
    t2 = state => destinationAddress === null ? undefined : state.cooldownEndsAtByAddress[destinationAddress];
    $[5] = destinationAddress;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const cooldownEndsAtMs = useFaucetCooldownStore(t2);
  const setCooldownEndsAt = useFaucetCooldownStore(_temp);
  const [nowMs, setNowMs] = useState(_temp2);
  const remainingCooldownSeconds = cooldownEndsAtMs === undefined ? 0 : Math.max(0, Math.ceil((cooldownEndsAtMs - nowMs) / 1000));
  const cooldownActive = remainingCooldownSeconds > 0;
  let t3;
  let t4;
  if ($[7] !== cooldownActive) {
    t3 = () => {
      if (!cooldownActive) {
        return;
      }
      const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
      return () => window.clearInterval(timer);
    };
    t4 = [cooldownActive];
    $[7] = cooldownActive;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  if (network === "mainnet") {
    return null;
  }
  if (viewerMode !== "own") {
    return null;
  }
  let t5;
  if ($[10] !== setCooldownEndsAt) {
    t5 = function startCooldown(walletAddress, seconds) {
      if (seconds === null || seconds <= 0) {
        return;
      }
      const startedAtMs = Date.now();
      setNowMs(startedAtMs);
      setCooldownEndsAt(walletAddress, startedAtMs + seconds * 1000);
    };
    $[10] = setCooldownEndsAt;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const startCooldown = t5;
  let t6;
  if ($[12] !== claim || $[13] !== cooldownActive || $[14] !== destination || $[15] !== startCooldown) {
    t6 = function handleClick() {
      if (destination === null || cooldownActive) {
        return;
      }
      const walletAddress_0 = destination.address;
      claim(walletAddress_0, {
        onClaimed: result => {
          startCooldown(walletAddress_0, result.cooldownSeconds);
        },
        onFailed: failure => {
          if (failure.isCooldown) {
            startCooldown(walletAddress_0, failure.cooldownSeconds);
          }
        }
      });
    };
    $[12] = claim;
    $[13] = cooldownActive;
    $[14] = destination;
    $[15] = startCooldown;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const handleClick = t6;
  let t7;
  if ($[17] !== destination || $[18] !== isPending || $[19] !== t) {
    t7 = destination === null ? t("select_wallet") : isPending ? t("claiming") : t("claim_button");
    $[17] = destination;
    $[18] = isPending;
    $[19] = t;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const claimLabel = t7;
  let t8;
  if ($[21] !== cooldownActive || $[22] !== remainingCooldownSeconds || $[23] !== t || $[24] !== tFaucet) {
    const cooldown = cooldownDisplay(cooldownActive ? remainingCooldownSeconds : null);
    t8 = !cooldownActive ? t("description") : cooldown.unit === "hours" ? tFaucet("cooldown_hours", {
      hours: cooldown.value
    }) : cooldown.unit === "minutes" ? tFaucet("cooldown_minutes", {
      minutes: cooldown.value
    }) : tFaucet("cooldown_soon");
    $[21] = cooldownActive;
    $[22] = remainingCooldownSeconds;
    $[23] = t;
    $[24] = tFaucet;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const description = t8;
  let t9;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="pointer-events-none absolute top-1/2 left-0 z-0 -translate-x-6 -translate-y-1/2" aria-hidden="true"><div className="relative size-20 overflow-hidden rounded-[var(--radius-control)]"><Image alt="" src="/images/usdc.png" fill={true} sizes="80px" className="object-contain object-center drop-shadow-sm select-none" /></div></div>;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t) {
    t10 = t("title");
    $[27] = t;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10) {
    t11 = <span className="text-body font-medium">{t10}</span>;
    $[29] = t10;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== description) {
    t12 = <span className="text-caption text-foreground-secondary">{description}</span>;
    $[31] = description;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t11 || $[34] !== t12) {
    t13 = <div className="flex min-w-0 flex-1 flex-col gap-0.5 break-words">{t11}{t12}</div>;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const t14 = isPending || cooldownActive || destination === null;
  let t15;
  if ($[36] !== claimLabel || $[37] !== handleClick || $[38] !== isPending || $[39] !== t14) {
    t15 = <AnimatedButton variant="primary" size="default" disableHoverScale={true} disableTapScale={true} className="shrink-0 overflow-hidden rounded-panel-row" onClick={handleClick} disabled={t14} loading={isPending}>{claimLabel}</AnimatedButton>;
    $[36] = claimLabel;
    $[37] = handleClick;
    $[38] = isPending;
    $[39] = t14;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t13 || $[42] !== t15) {
    t16 = <Panel className="min-w-0 flex-1 overflow-hidden"><PanelContent className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">{t9}<div className="relative z-[1] flex min-w-0 flex-1 flex-col gap-3 pl-[calc(theme(spacing.20)+0.75rem-1rem-1.5rem)] sm:flex-row sm:items-center sm:justify-between">{t13}{t15}</div></PanelContent></Panel>;
    $[41] = t13;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  return t16;
}
function _temp2() {
  return Date.now();
}
function _temp(state_0) {
  return state_0.setCooldownEndsAt;
}